// Section-by-section completeness check + strict payroll readiness gate.
// Loose Employee type: backend field names vary during migration.

export type SectionId =
  | 'personal'
  | 'emergency'
  | 'employment'
  | 'payment_method'
  | 'base_pay'
  | 'additional_pay'
  | 'time_off'
  | 'tax'
  | 'banking'
  | 'deductions';

export interface Employee {
  id?: string | number;
  first_name?: string;
  last_name?: string;
  email?: string;
  hire_date?: string;
  work_location?: string;
  work_location_id?: string | number;
  job_title?: string;
  position_title?: string;
  employee_id?: string;
  payment_method?: string;
  account_type?: string;
  pay_type?: string;
  hourly_rate?: string | number;
  salary_amount?: string | number;
  additional_pay_types?: unknown[];
  vacation_policy?: unknown;
  sick_pay_policy?: unknown;
  sick_pay?: unknown;
  unpaid_time_off_policy?: unknown;
  unpaid_time_off?: unknown;
  federal_td1_amount?: unknown;
  federal_claim_amount?: unknown;
  federal_credit_amount?: unknown;
  provincial_claim_amount?: unknown;
  provincial_credit_amount?: unknown;
  cpp_exempt?: unknown;
  ei_exempt?: unknown;
  federal_income_tax_exempt?: unknown;
  bank_name?: string;
  account_number?: string;
  deductions?: unknown[];
  t4_dental_benefits_codes?: unknown;
  sin_or_ssn?: string;
  sin?: string;
  social_insurance_number?: string;
  address_line1?: string;
  address_line_1?: string;
  street_address?: string;
  city?: string;
  province_or_state?: string;
  province?: string;
  province_state?: string;
  postal_or_zip?: string;
  postal_code?: string;
  postal_zip?: string;
  work_city?: string;
  start_date?: string;
  emergency_name?: string;
  emergency_home_phone?: string;
  emergency_mobile_phone?: string;
  emergency_email?: string;
  status?: string;
  [key: string]: unknown;
}

export interface MissingItem {
  section: SectionId;
  label: string;
}

export interface ReadinessResult {
  ready: boolean;
  missing: MissingItem[];
}

// Section-by-section completeness check (drives Edit/Start labels).
export const isSectionFilled = (sectionId: SectionId, emp: Employee | null | undefined): boolean => {
  if (!emp) return false;
  switch (sectionId) {
    case "personal":
      return !!(emp.first_name || emp.last_name || emp.email);
    case "emergency":
      return !!(
        emp.emergency_name ||
        emp.emergency_home_phone ||
        emp.emergency_mobile_phone ||
        emp.emergency_email
      );
    case "employment":
      return !!(emp.hire_date || emp.work_location || emp.work_location_id || emp.job_title || emp.employee_id);
    case "payment_method":
      return !!emp.payment_method;
    case "base_pay":
      return !!emp.pay_type && (parseFloat(String(emp.hourly_rate)) > 0 || parseFloat(String(emp.salary_amount)) > 0);
    case "additional_pay":
      return Array.isArray(emp.additional_pay_types) && emp.additional_pay_types.length > 0;
    case "time_off":
      return !!(
        emp.vacation_policy ||
        emp.sick_pay_policy ||
        emp.sick_pay ||
        emp.unpaid_time_off_policy ||
        emp.unpaid_time_off
      );
    case "tax":
      return !!(
        emp.federal_td1_amount ||
        emp.federal_claim_amount ||
        emp.federal_credit_amount ||
        emp.provincial_claim_amount ||
        emp.provincial_credit_amount ||
        emp.cpp_exempt ||
        emp.ei_exempt ||
        emp.federal_income_tax_exempt
      );
    case "banking":
      return !!(emp.bank_name || emp.account_number);
    case "deductions":
      return (
        (Array.isArray(emp.deductions) && emp.deductions.length > 0) ||
        !!emp.t4_dental_benefits_codes
      );
    default:
      return false;
  }
};

// Payroll readiness: stricter than isSectionFilled, requires the specific fields
// needed to actually run a pay run. Returns { ready, missing: [{section, label}] }
// where section is the EmployeeProfile section id to deep-link into.
export const getReadiness = (emp: Employee | null | undefined): ReadinessResult => {
  if (!emp) return { ready: false, missing: [{ section: "personal", label: "personal info" }] };
  const missing: MissingItem[] = [];

  // 1. Name
  if (!emp.first_name || !emp.last_name) {
    missing.push({ section: "personal", label: "name" });
  }

  // 2. SIN
  if (!emp.sin_or_ssn && !emp.sin && !emp.social_insurance_number) {
    missing.push({ section: "personal", label: "SIN" });
  }

  // 3. Address (all four parts)
  const hasAddress = !!(emp.address_line1 || emp.street_address || emp.address_line_1) && !!emp.city && !!(emp.province_or_state || emp.province || emp.province_state) && !!(emp.postal_or_zip || emp.postal_code || emp.postal_zip);
  if (!hasAddress) {
    missing.push({ section: "personal", label: "address" });
  }

  // 4. Base pay
  const hasBasePay = !!emp.pay_type && (parseFloat(String(emp.hourly_rate)) > 0 || parseFloat(String(emp.salary_amount)) > 0);
  if (!hasBasePay) {
    missing.push({ section: "base_pay", label: "pay rate" });
  }

  // 5. Employment
  const hasEmployment = !!(emp.position_title || emp.job_title) && !!(emp.work_city || emp.work_location || emp.work_location_id) && !!(emp.start_date || emp.hire_date);
  if (!hasEmployment) {
    missing.push({ section: "employment", label: "employment details" });
  }

  // 6. Payment method
  const hasPayment = !!(emp.payment_method || emp.account_type);
  if (!hasPayment) {
    missing.push({ section: "payment_method", label: "payment method" });
  }

  return { ready: missing.length === 0, missing };
};
